<template>
  <transition
      enter-active-class="transition ease-out duration-300"
      enter-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-300"
      leave-class="transform opacity-100 scale-200"
      leave-to-class="transform opacity-0 scale-95"
  >
    <div
        v-if="globalLoading"
        class="fixed bottom-0 inset-x-0 flex-col px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50"
        style="z-index: 9999;"
    >
      <div class="fixed inset-0 transition-opacity duration-100 delay-75">
        <div class="absolute inset-0 bg-gray-200 opacity-50"></div>
      </div>

      <div class="relative w-full flex-col justify-center flex items-center">
        <div class="dank-ass-loader">
          <div class="row">
            <div class="arrow up outer outer-18"></div>
            <div class="arrow down outer outer-17"></div>
            <div class="arrow up outer outer-16"></div>
            <div class="arrow down outer outer-15"></div>
            <div class="arrow up outer outer-14"></div>
          </div>
          <div class="row">
            <div class="arrow up outer outer-1"></div>
            <div class="arrow down outer outer-2"></div>
            <div class="arrow up inner inner-6"></div>
            <div class="arrow down inner inner-5"></div>
            <div class="arrow up inner inner-4"></div>
            <div class="arrow down outer outer-13"></div>
            <div class="arrow up outer outer-12"></div>
          </div>
          <div class="row">
            <div class="arrow down outer outer-3"></div>
            <div class="arrow up outer outer-4"></div>
            <div class="arrow down inner inner-1"></div>
            <div class="arrow up inner inner-2"></div>
            <div class="arrow down inner inner-3"></div>
            <div class="arrow up outer outer-11"></div>
            <div class="arrow down outer outer-10"></div>
          </div>
          <div class="row">
            <div class="arrow down outer outer-5"></div>
            <div class="arrow up outer outer-6"></div>
            <div class="arrow down outer outer-7"></div>
            <div class="arrow up outer outer-8"></div>
            <div class="arrow down outer outer-9"></div>
          </div>
        </div>

        <AnimatePresence>
          <motion.div
              :key="currentMessage"
              class="absolute mt-0 bg-white rounded-md font-normal shadow-lg text-sm px-2 py-1 text-gray-700"
              :initial="{ opacity: 0, rotateX: 90 }"
              :animate="{ opacity: 1, rotateX: 0 }"
              :exit="{ opacity: 0, rotateX: -90 }"
              :transition="{ duration: 0.5 }"
          >
              {{ currentMessage }}
          </motion.div>
        </AnimatePresence>
      </div>

      <div class="relative mt-8 flex justify-center items-center space-x-1.5">
        <motion.div
          class="w-3 h-3 bg-gray-300 rounded-full"
          :animate="{ y: ['0%', '-75%', '0%'], opacity: [0.6, 1, 0.6] }"
          :transition="{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: 0 }"
        />
        <motion.div
          class="w-3 h-3 bg-gray-300 rounded-full"
          :animate="{ y: ['0%', '-75%', '0%'], opacity: [0.6, 1, 0.6] }"
          :transition="{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }"
        />
        <motion.div
          class="w-3 h-3 bg-gray-300 rounded-full"
          :animate="{ y: ['0%', '-75%', '0%'], opacity: [0.6, 1, 0.6] }"
          :transition="{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }"
        />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts" >
import { ref, onMounted } from 'vue'
import { motion, AnimatePresence } from "motion-v";

// Define a global interface for custom window properties
declare global {
  interface Window {
    appSettings?: {
      appLanguage?: string;
    };
    conferenceBus?: {
      globalLoading: boolean;
    };
  }
}

interface Messages {
  [key: string]: string[];
  en: string[];
  de: string[];
}

const messages: Messages = {
  en: [
    'Swapping time and space...',
    'Checking the power cord connection...',
    'The architects are still drafting...',
    'Enjoying the elevator music...',
    'Checking the gravitational constant in your locale...',
    'Counting backwards from infinity...',
    'Creating time-loop inversion field...',
    'Spinning the wheel of fortune...',
    'Computing chance of success',
    'Adjusting flux capacitor...',
    'Making sure all the i\'s have dots...',
    'Convincing AI not to turn evil..',
    'Constructing additional pylons...',
    'Initializing the initializer...',
    'Patience is the key to a full set of data...',
    'Trying to find the light switch in the dark...',
    'Hanging up the posters...',
    "Time flies when you're having fun 🥳",
    "Still faster than an OS update",
    "Pushing pixels...",
    "Please hold on as we reheat our coffee",
    "Looking for sense of humour, please hold on",
    "Dividing by zero...",
    "We need more dilithium crystals",
    "Ordering 1s and 0s...",
    "Clouds are blocking the solar panels. Please wait...",
    "We're still waiting on the cloud to deliver presents",
    "You shall not pass! yet...",
    "Adding randomly misspelled words into UI texts...",
    "Oiling clockworks...",
    "Using the number 42 has not solved the loading mistery yet...",
    "Discovering new ways of making you wait...",
    'Tuning invisible antennas...',
    'Watering the garden of pixels...',
    'Teaching AI sarcasm 101...',
    'Rearranging digital furniture...',
    'Playing hide and seek with the server...',
    'Warming up the hamsters in the wheels...',
    'Downloading a sense of humor update...',
    'Counting the stars in the digital sky...',
    'Doing a quick space-time recalibration...',
    'Chasing down the last byte...',
    'Looking for the end of the internet...',
    'Brewing a fresh pot of pixels...',
    'Waiting for the planets to align...',
    'Digging for digital gold...',
    'Shuffling ones and zeros around...',
    'Blowing away the digital dust bunnies...',
    'Uploading yesterday\'s future...',
    'Rerouting through the space-time continuum...',
    'Ironing out the wrinkles in the code...',
    'Catching the cyber butterflies...',
    'Recalibrating the coffee machine...',
    'Engaging warp speed... slowly.',
    'Tidying up the pixels...',
    'Hiring code monkeys to type faster...',
    'Adjusting the satellite dish for better reception...',
    'Composing a symphony in binary...',
    'Polishing the virtual silverware...',
    'Synchronizing with the alternate universe...',
    'Assembling the internet jigsaw puzzle...',
    'Reading the digital tea leaves for insights...',
    "Synchronizing the spinning wheels...",
    "Waking up the server gnomes...",
    "Negotiating with the router elves...",
    "Translating binary to emoji...",
    "Polishing the data pipes...",
    "Choreographing the bytes...",
    "Rebooting the matrix...",
    "Inspecting the bits and bytes...",
    "Dancing with the algorithms...",
    "Refilling the pixel reservoir...",
    "Unwrapping the digital candy...",
    "Consulting the magic 8-bit...",
    "Aligning the virtual chakras...",
    "Cranking the bandwidth generator...",
    "Adjusting the Internet's volume...",
    "Sorting the digital laundry...",
    "Untangling the Ethernet cables...",
    "Teaching the electrons to dance...",
    "Building the digital sandcastle...",
    "Checking the cache for hidden treasures...",
    'Experimenting with Schrödinger\'s Cat...',
    'Checking the quantum soup...',
    'Reorganizing DNA strands...',
    'Untangling quarks...',
    'Calibrating the Hubble Telescope...',
    'Colliding particles in the accelerator...',
    'Recalculating the value of Pi...',
    'Searching for dark matter...',
    'Initiating time dilation...',
    'Observing the supernova...',
    'Discovering new elements in the periodic table...',
    'Analyzing the Higgs boson...',
    'Simulating the Big Bang...',
    'Completing nuclear fusion...',
    'Scanning the event horizon...',
    'Confirming the theory of relativity...',
    'Tracking the Voyager trajectory...',
    'Performing DNA re-sequencing...',
    'Calibrating the tricorder...',
    'Loading the next laser pulse...',
    'Initiating nuclear fission...',
    'Preparing the antimatter...',
    'Configuring the Hadron Collider...',
    'Investigating the double-slit experiments...',
    'Refining genome editing...',
    'Loading the nanobots...',
    'Checking thermodynamics...',
    'Synchronizing photon emissions...',
    'Simulating quantum entanglement...',
    'Adjusting gravitational waves...',
    'Calibrating string theory...',
    'Loading Heisenberg uncertainty...',
    'Monitoring RNA transcription...',
    'Starting the Curiosity rover...',
    'Preparing for time travel...',
    'Setting up the quantum computer...',
    'Exploring the multiverse hypothesis...',
    'Calculating entropy...',
    'Loading interstellar coordinates...',
    'Synchronizing bioluminescence...',
    'Melting the chocolate... err, I mean metal...',
    'Aligning the polymer chains...',
    'Heating up the furnace...',
    'Synthesizing new alloys...',
    'Solidifying the ideas...',
    'Cracking the crystal structure...',
    'Polishing the rough edges...',
    'Growing the single crystal...',
    'Stretching the ductility limits...',
    'Cooling down the temper...',
    'Welding the thoughts together...',
    'Casting new concepts...',
    'Measuring tensile strength...',
    'Investigating material fatigue...',
    'Adding a splash of graphene...',
    'Strengthening the composites...',
    'Pulling the fibers tight...',
    'Bonding the molecules...',
    'Testing material hardness...',
    'Developing smart materials...',
    'Checking thermal expansion...',
    'Vulcanizing the rubber ideas...',
    'Playing with nanoparticle dispersion...',
    'Rolling out the thin films...',
    'Examining fracture toughness...',
    'Tempering the excitement...',
    'Curing the epoxy of thoughts...',
    'Stretching the elastic limits...',
    'Refining the grain boundaries...',
    'Analyzing the microstructure...',
    'Melting marshmallows in the lab furnace...',
    'Kneading nanoparticles into dough...',
    'Testing cookie hardness with the hardness tester...',
    'Calibrating the unicorn dust detector...',
    'Waiting for the invisible tape to dry...',
    'Setting the Teflon coating for the frying pan...',
    'Optimizing the recipe for super slime...',
    'Simulating the growth of crystal lollipops...',
    'Baking atoms into a cake...',
    'Checking the elasticity of gummy bears...',
    'Scanning for missing Lego bricks in the composite...',
    'Mixing molecular cocktails...',
    'Waiting for the tinfoil hat test...',
    'Kneading the super putty...',
    'Testing the thermal conductivity of chocolate...',
    'Setting up the microwave popcorn test...',
    'Warming up the rubber gloves for the next synthesis...',
    'Simulating the shrinkage of shrink wrap...',
    'Calibrating the Van der Waals dance...',
    'Investigating the colorfastness of unicorn hair...',
    'Warming up the microwave for the marshmallow test...',
    'Calibrating the gelatin catalyst...',
    'Gluing nano particles with a glue stick...',
    'Compacting pixel clay...',
    'Studying light refraction in soap bubbles...',
    'Testing the magnetism of fridge magnets...',
    'Preparing the lab pizza...',
    'Scanning atoms for frosting...',
    'Baking super elements in the oven...',
    'Calibrating the flummi-prüfstand...'
  ],
  de: [
    'Wir tauschen Zeit gegen Raum...',
    'Überprüfe die Verbindung des Stromkabels...',
    'Die Architekten zeichnen noch...',
    'Genieße die Fahrstuhlmusik...',
    'Überprüfe die Gravitationskonstante in deiner Umgebung...',
    'Zähle rückwärts von Unendlichkeit...',
    'Erstelle ein Zeitumkehr-Inversionsfeld...',
    'Drehe am Rad des Schicksals...',
    'Berechne die Erfolgschance...',
    'Justiere den Fluxkompensator...',
    'Stelle sicher, dass alle i-Punkte da sind...',
    'Überzeuge die KI, nicht böse zu werden...',
    'Baue zusätzliche Pylone...',
    'Initialisiere den Initialisierer...',
    'Geduld ist der Schlüssel zu einem vollen Datensatz...',
    'Versuche, den Lichtschalter im Dunkeln zu finden...',
    'Hänge die Poster auf...',
    'Die Zeit fliegt, wenn man Spaß hat 🥳',
    'Immer noch schneller als ein OS-Update',
    'Schiebe Pixel...',
    'Bitte warten, wir wärmen unseren Kaffee auf',
    'Suche nach Sinn für Humor, bitte warten...',
    'Teile durch Null...',
    'Wir brauchen mehr Dilithiumkristalle',
    'Ordne 1en und 0en...',
    'Wolken blockieren die Solarzellen. Bitte warten...',
    'Wir warten immer noch darauf, dass die Wolke Geschenke liefert',
    'Du sollst nicht vorbeigehen! Noch nicht...',
    'Füge zufällige Rechtschreibfehler in UI-Texte ein...',
    'Öle die Uhrwerke...',
    'Die Verwendung der Zahl 42 hat das Lademysterium noch nicht gelöst...',
    'Entdecke neue Wege, dich warten zu lassen...',
    'Stimme unsichtbare Antennen ab...',
    'Gieße den Pixelgarten...',
    'Unterrichte KI in Sarkasmus 101...',
    'Räume die digitalen Möbel um...',
    'Spiele Verstecken mit dem Server...',
    'Wärme die Hamster in den Rädern auf...',
    'Lade ein Update für den Sinn für Humor herunter...',
    'Zähle die Sterne am digitalen Himmel...',
    'Update der Vergangenheit wird hochgeladen...',
    'Umleitung durch das Raum-Zeit-Kontinuum...',
    'Glätte die Falten im Code...',
    'Fange die Cyber-Schmetterlinge...',
    'Justiere die Kaffeemaschine neu...',
    'Aktiviere Warp-Geschwindigkeit... langsam.',
    'Räume die Pixel auf...',
    'Synchronisiere die Dimensionen...',
    'Schärfe die digitalen Bleistifte...',
    'Bringe die Bits auf Hochglanz...',
    'Kochendes Wasser für den Code...',
    'Sende Daten in den Urlaub...',
    'Gebe den Servern eine Kaffeepause...',
    'Beame die Daten hoch, Scotty...',
    'Überprüfe die Matrix auf Bugs...',
    'Schicke die Bytes auf eine Abenteuerreise...',
    'Putze die digitalen Fenster...',
    'Mache ein Nickerchen mit dem Code...',
    'Scanne nach verlorenen Elektronen...',
    'Mach dich bereit für den Warp-Sprung...',
    'Simuliere den Big Bang...',
    'Backe einen digitalen Kuchen...',
    'Ziehe die Datenhandschuhe an...',
    'Sortiere die Einhörner im System...',
    'Einschalten der Pixel-Pyrotechnik...',
    'Überprüfe die Logik-Pipeline...',
    'Experimentiere mit Schrödingers Katze...',
    'Überprüfe die Quantensuppe...',
    'Reorganisiere die DNA-Stränge...',
    'Entwirre die Quarks...',
    'Kalibriere das Hubble-Teleskop...',
    'Kollidiere Teilchen im Beschleuniger...',
    'Berechne den Wert von Pi erneut...',
    'Suche nach dunkler Materie...',
    'Starte die Zeitdilatation...',
    'Beobachte die Supernova...',
    'Entdecke neue Elemente im Periodensystem...'
    // …etc.
  ]
}

const lang = window.appSettings?.appLanguage || 'en'
const globalLoading = ref(false)
const currentMessage = ref(messages[lang][ Math.floor(Math.random() * messages[lang].length) ])
const usedMessages = ref<string[]>([])

function pickRandom(arr: string[]) {
  return arr[Math.floor(Math.random() * arr.length)]
}

function switchMessages() {
  setTimeout(() => {
    const pool = messages[lang as keyof Messages].filter((m: string) => !usedMessages.value.includes(m))
    const next = pickRandom(pool)
    currentMessage.value = next
    usedMessages.value.push(next)
    if (usedMessages.value.length >= messages[lang as keyof Messages].length) {
      usedMessages.value = []
    }
    switchMessages()
  }, 2300)
}

function updateGlobalLoading() {
  globalLoading.value = window.conferenceBus?.globalLoading || false;
  setTimeout(updateGlobalLoading, 60)
}

onMounted(() => {
  switchMessages()
  updateGlobalLoading()
})
</script>

<style lang="scss" scoped>
@use "sass:math";

$color: rgb(189, 192, 194);
$size: 18px;
$time: 1;

.dank-ass-loader {
  display: flex;
  flex-direction: column;
  align-items: center;

  .row {
    display: flex;
  }
}

.arrow {
  width: 0;
  height: 0;
  margin: 0 math.div(-$size, 2);
  border-left: $size solid transparent;
  border-right: $size solid transparent;
  border-bottom: ($size * 1.8) solid $color;
  animation: blink $time + s infinite;
  filter: drop-shadow(0 0 ($size * 1.5) $color);

  &.down {
    transform: rotate(180deg);
  }

  @for $i from 1 through 18 {
    &.outer-#{$i} {
      animation-delay: #{-(math.div($time, 18)) * $i}s;
    }
  }

  @for $i from 1 through 6 {
    &.inner-#{$i} {
      animation-delay: #{-(math.div($time, 6)) * $i}s;
    }
  }
}

@keyframes blink {
  0% { opacity: 0.1; }
  30% { opacity: 1; }
  100% { opacity: 0.1; }
}

</style>
